import React, { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useArchiveContactMutation,
  useArchiveOrganizationMutation,
} from "../../../slices/ContactsSlice";
import { useGetClientOverviewQuery } from "../../../slices/ReportDataSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import {
  modalCancel,
  modalPrimary,
  PortalModal,
} from "../../clientPortal/PortalModal";
import { ComposeEmailModal } from "../../contacts/modals/ComposeEmailModal";
import { FilterMenu } from "../../works/board/FilterMenu";
import { askTable } from "../ask/reportAsk.utils";
import {
  billedByClient,
  lastActivityText,
  QUIET_OPTIONS,
  quietClients,
  quietForText,
} from "../moreReports.utils";
import { formatMoney, presetRange } from "../reports.utils";
import { useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import { ChoiceMenu } from "../ui/ReportFilters";
import {
  Card,
  Empty,
  LINK,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  Segmented,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllInvoices } from "../useReportData";

type TypeFilter = "all" | "organization" | "individual";
const TYPES: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "organization", label: "Organizations" },
  { value: "individual", label: "Individuals" },
];
const TABLE = "Quiet clients";

/** Quiet clients (2026-11-05): active clients with no activity for 3 / 6 / 12 months. */
export const QuietClientsReport: FC = () => {
  const access = useReportsAccess();
  const orgId = getLinkedFirm()?.orgId || "";
  const [monthsText, setMonths] = useViewExtra(
    "months",
    "6",
    QUIET_OPTIONS,
    "Quiet for",
  );
  const [type, setType] = useViewExtra<TypeFilter>(
    "type",
    "all",
    TYPES,
    "Type",
  );
  const [owners, setOwners] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveContact] = useArchiveContactMutation();
  const [archiveOrganization] = useArchiveOrganizationMutation();
  const overview = useGetClientOverviewQuery({ orgId }, { skip: !orgId });
  const lastYear = useMemo(() => presetRange("lastYear"), []);
  const invoices = useAllInvoices(
    { from: lastYear?.from ?? null, to: lastYear?.to ?? null },
    !access.billing,
  );
  const billed = useMemo(() => billedByClient(invoices.data), [invoices.data]);
  const months = Number(monthsText) || 6;
  const rows = useMemo(
    () =>
      quietClients(overview.data?.clients ?? [], months).filter(
        (c) =>
          (type === "all" || c.type === type) &&
          (!owners.length || owners.includes(c.owner?.id || "none")),
      ),
    [overview.data, months, type, owners],
  );
  const ownerOptions = useMemo(() => {
    const map = new Map<string, string>();
    (overview.data?.clients ?? []).forEach((c) =>
      map.set(c.owner?.id || "none", c.owner?.name || "Not set"),
    );
    return Array.from(map, ([value, label]) => ({ value, label })).sort(
      (a, b) => a.label.localeCompare(b.label),
    );
  }, [overview.data]);
  const picked = rows.filter((r) => selected.includes(r.customerId));
  const withEmail = picked.filter((r) => r.email);
  const shown = useFocusRows(TABLE, rows, (r) => r.customerId);
  const { pageRows, pager } = usePagedRows(shown);
  const allOnPage =
    pageRows.length > 0 &&
    pageRows.every((r) => selected.includes(r.customerId));
  const toggle = (id: string) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const onArchive = async () => {
    setArchiving(true);
    let done = 0;
    for (const r of picked) {
      const result = r.businessContactId
        ? await archiveOrganization({ id: r.businessContactId })
        : r.contactId
        ? await archiveContact({ contactId: r.contactId })
        : null;
      if (result && !("error" in result)) done += 1;
    }
    setArchiving(false);
    setConfirmOpen(false);
    setSelected([]);
    overview.refetch();
    if (done === picked.length)
      toast.success(`${done} client${done === 1 ? "" : "s"} archived.`);
    else
      toast.error(
        `${done} of ${picked.length} clients were archived. Please try the others again.`,
      );
  };

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        Client: r.name,
        Type: r.type === "organization" ? "Organization" : "Individual",
        "Quiet for": quietForText(r.quietMonths),
        "Last activity": lastActivityText(r),
        "Client owner": r.owner?.name || "Not set",
        ...(access.billing
          ? { "Billed last year": Math.round(billed.get(r.customerId) || 0) }
          : {}),
        Email: r.email || "",
      })),
      "Quiet clients",
    );

  const ask = useMemo(
    () => ({
      title: "Quiet clients",
      path: "/reports/quiet-clients",
      description: `Active clients with no work, time, invoices, payments, notes, files or portal uploads for ${months} months or more.`,
      kpis: [{ label: "Quiet clients", value: String(rows.length) }],
      tables: [
        askTable(
          TABLE,
          rows,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "months",
              label: "Quiet for (months)",
              kind: "number",
              value: (r) => (r.quietMonths >= 999 ? null : r.quietMonths),
            },
            {
              key: "last",
              label: "Last activity",
              kind: "text",
              value: (r) => lastActivityText(r),
            },
            {
              key: "owner",
              label: "Client owner",
              kind: "text",
              value: (r) => r.owner?.name || "Not set",
            },
            ...(access.billing
              ? [
                  {
                    key: "billed",
                    label: "Billed last year",
                    kind: "money" as const,
                    value: (r: typeof rows[number]) =>
                      billed.get(r.customerId) || 0,
                  },
                ]
              : []),
          ],
          (r) => r.customerId,
          (r) => r.link,
        ),
      ],
    }),
    [rows, months, billed, access.billing],
  );

  const isLoading =
    overview.isLoading || (access.billing && invoices.isLoading);

  return (
    <ReportPage
      title={"Quiet clients"}
      description={
        "Active clients with no work, time, invoices or emails for a while: follow up, or archive them."
      }
      onExport={rows.length ? onExport : undefined}
      ask={isLoading ? undefined : ask}
      askSuggestions={[
        "Which quiet clients billed the most last year?",
        "Who has been quiet the longest?",
        "Which of Joe's clients are quiet?",
      ]}
      filters={
        <>
          <Segmented
            label={"Quiet for"}
            options={QUIET_OPTIONS}
            value={monthsText}
            onChange={setMonths}
          />
          <FilterMenu
            placeholder={"Client owner"}
            options={ownerOptions}
            selected={owners}
            onChange={setOwners}
          />
          <ChoiceMenu
            label={"Type"}
            options={TYPES}
            value={type}
            onChange={setType}
          />
        </>
      }>
      {(overview.isError || invoices.isError) && <LoadError />}

      {picked.length > 0 && (
        <div
          className={
            "flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#D9D1FF] bg-[#F5F3FF] px-4 py-3"
          }>
          <span className={"text-[13.5px] font-bold text-[#3F2EA8]"}>
            {picked.length} client{picked.length === 1 ? "" : "s"} selected
          </span>
          <div className={"flex flex-wrap gap-2"}>
            <button
              type={"button"}
              disabled={!withEmail.length}
              title={
                withEmail.length
                  ? undefined
                  : "None of them has an email address"
              }
              onClick={() => setComposeOpen(true)}
              className={
                "h-9 rounded-[9px] border border-[#6B4EFF] bg-white px-3.5 text-[13px] font-bold text-[#4B32D6] hover:bg-[#F0EDFF] disabled:opacity-50"
              }>
              Email them
            </button>
            <button
              type={"button"}
              onClick={() => setConfirmOpen(true)}
              className={
                "h-9 rounded-[9px] border border-[#DCE2EA] bg-white px-3.5 text-[13px] font-bold text-[#B42318] hover:border-[#B42318]"
              }>
              Archive
            </button>
          </div>
        </div>
      )}

      <Card
        title={`${rows.length} quiet client${rows.length === 1 ? "" : "s"}`}>
        {isLoading ? (
          <Loading />
        ) : !rows.length ? (
          <Empty
            text={`No active client has been quiet for ${months} months.`}
          />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={`${TH} w-10`}>
                  <input
                    type={"checkbox"}
                    aria-label={"Select all on this page"}
                    checked={allOnPage}
                    onChange={() =>
                      setSelected((s) =>
                        allOnPage
                          ? s.filter(
                              (id) =>
                                !pageRows.some((r) => r.customerId === id),
                            )
                          : Array.from(
                              new Set([
                                ...s,
                                ...pageRows.map((r) => r.customerId),
                              ]),
                            ),
                      )
                    }
                    className={"h-4 w-4 accent-[#6B4EFF]"}
                  />
                </th>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  Quiet for
                </th>
                <th scope={"col"} className={TH}>
                  Last activity
                </th>
                <th scope={"col"} className={TH}>
                  Client owner
                </th>
                {access.billing && (
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Billed last year
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr
                  key={r.customerId}
                  className={
                    selected.includes(r.customerId) ? "bg-[#FBFAFF]" : undefined
                  }>
                  <td className={TD}>
                    <input
                      type={"checkbox"}
                      aria-label={r.name}
                      checked={selected.includes(r.customerId)}
                      onChange={() => toggle(r.customerId)}
                      className={"h-4 w-4 accent-[#6B4EFF]"}
                    />
                  </td>
                  <td className={TD}>
                    {r.link ? (
                      <Link to={r.link} className={LINK}>
                        {r.name}
                      </Link>
                    ) : (
                      <span className={"font-bold"}>{r.name}</span>
                    )}
                  </td>
                  <td
                    className={`${TD} font-bold ${
                      r.quietMonths >= 12 ? "text-[#B42318]" : "text-[#B54708]"
                    }`}>
                    {quietForText(r.quietMonths)}
                  </td>
                  <td className={`${TD} text-[#3F4757]`}>
                    {lastActivityText(r)}
                  </td>
                  <td className={`${TD} ${r.owner ? "" : "text-[#8A90A2]"}`}>
                    {r.owner?.name || "Not set"}
                  </td>
                  {access.billing && (
                    <td className={`${TD} ${NUM}`}>
                      {formatMoney(billed.get(r.customerId) || 0)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Activity = work created or its status changed, time, invoices, payments,
        notes, files, client portal uploads, signature requests and changes in
        the Activity Log. Emails aren&apos;t counted yet.
        {!access.firmWide && " You see your own clients."}
      </Note>

      {composeOpen && (
        <ComposeEmailModal
          isActive={composeOpen}
          onClose={() => setComposeOpen(false)}
          bulk={true}
          customersEmail={withEmail.map((r) => ({ email: r.email as string }))}
        />
      )}

      {confirmOpen && (
        <PortalModal
          title={`Archive ${picked.length} client${
            picked.length === 1 ? "" : "s"
          }?`}
          subtitle={
            "They move to Contacts > Archived. You can reactivate them there at any time."
          }
          onClose={() => !archiving && setConfirmOpen(false)}
          width={480}
          footer={
            <>
              <button
                type={"button"}
                className={modalCancel}
                disabled={archiving}
                onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button
                type={"button"}
                className={modalPrimary}
                disabled={archiving}
                onClick={onArchive}>
                {archiving ? "Archiving…" : "Archive"}
              </button>
            </>
          }>
          <ul className={"flex flex-col gap-1 text-[13.5px]"}>
            {picked.slice(0, 12).map((r) => (
              <li key={r.customerId} className={"font-semibold"}>
                {r.name}
              </li>
            ))}
            {picked.length > 12 && (
              <li className={"text-[#5B6472]"}>
                and {picked.length - 12} more
              </li>
            )}
          </ul>
        </PortalModal>
      )}
    </ReportPage>
  );
};
